import { css } from '@emotion/react';
import styled, { StyledComponent } from '@emotion/styled';
import { MEDIA_QUERY } from '@zola/zola-ui/src/styles/emotion';
import type { SerializedStyles } from '@emotion/react';
import _pick from 'lodash/pick';
import {
  WButtonStylesView,
  WFontView,
  ButtonPseudoClassStyles,
  ResponsiveAssetStyles,
  WAbsoluteAssetStyles,
  WInFlowAssetStyles,
  WAssetBaseStyles,
  WPseudoElementView,
  CustomBreakpoint,
  WWidthView,
  WBackgroundView,
} from '../../mockData/theme_styles/types';

const mapFontSizeToCSSValue = (size: number) => `
font-size: ${size}px;
${MEDIA_QUERY.TABLET} {
  font-size: ${Math.ceil(size * 0.8)}px;
}
${MEDIA_QUERY.MOBILE} {
  font-size ${Math.ceil(size * 0.6)}px;
}
`;

export const mapFontViewToSerializedStyles = (font: WFontView): SerializedStyles => {
  const { font_family, font_color, font_size, font_weight, line_height, text_transform } = font;
  const responsiveFontSizeStyling = mapFontSizeToCSSValue(font_size);
  return css`
    font-family: ${font_family};
    color: ${font_color};
    font-weight: ${font_weight};
    text-transform: ${text_transform};
    line-height: ${line_height};
    ${responsiveFontSizeStyling};
    margin: 0;
  `;
};

export const mapButtonStateStyles = ({
  background_color,
  font_color,
  border,
  box_shadow,
}: ButtonPseudoClassStyles): string => `
  background-color: ${background_color};
  color: ${font_color};
  ${border && `border: ${border}`};
  ${box_shadow && `box-shadow: ${box_shadow}`};
`;

export const mapButtonStylesToComponent = (
  styles: WButtonStylesView
): StyledComponent<React.ButtonHTMLAttributes<HTMLButtonElement>> => {
  const {
    font,
    background_color,
    border,
    border_radius,
    box_shadow,
    pseudo_active,
    pseudo_disabled,
    pseudo_hover,
  } = styles;

  return styled.button`
    ${mapFontViewToSerializedStyles(font)};
    background-color: ${background_color};
    border: ${border || 'none'};
    ${box_shadow && `box-shadow: ${box_shadow}`};
    border-radius: ${border_radius};
    padding: 16px;
    &:active {
      ${pseudo_active && mapButtonStateStyles(pseudo_active)}
    }
    &:hover {
      ${pseudo_hover && mapButtonStateStyles(pseudo_hover)}
    }
    &:disabled {
      ${pseudo_disabled && mapButtonStateStyles(pseudo_disabled)}
    }
  `;
};

const mapWidthToCSS = (w?: WWidthView): string => {
  if (!w) {
    return '';
  }
  const { unit, value } = w;
  if (unit === 'percentage') {
    return `width: ${value}%;`;
  }
  if (unit === 'rem') {
    return `width: ${value}rem`;
  }
  return `width: ${value}px`;
};

const mapBaseStylesToCSS = (b: WAssetBaseStyles) => {
  const { width, zIndex, marginTop, marginBottom, marginRight, marginLeft } = b;
  // TODO: move responsive to where called
  return `
    ${mapWidthToCSS(width)};
    ${zIndex ? `z-index: ${zIndex};` : ''}
    ${marginTop ? `margin-top: ${marginTop};` : ''}
    ${marginBottom ? `margin-bottom: ${marginBottom};` : ''}
    ${marginRight ? `margin-right: ${marginRight};` : ''}
    ${marginLeft ? `margin-left: ${marginLeft};` : ''}
  `;
};

const mapAbsoluteAssetStyleToCSS = (abs: WAbsoluteAssetStyles) => {
  const {
    width,
    zIndex,
    top,
    bottom,
    right,
    left,
    marginTop,
    marginBottom,
    marginRight,
    marginLeft,
  } = abs;
  return `
    ${mapBaseStylesToCSS({
      width,
      zIndex,
      marginTop,
      marginBottom,
      marginRight,
      marginLeft,
    })}
    position: absolute;
    ${top ? `top: ${top};` : ''}
    ${bottom ? `bottom: ${bottom};` : ''}
    ${right ? `right: ${right};` : ''}
    ${left ? `left: ${left};` : ''}
  `;
};

const mapInFlowStylesToCSS = (inf: WInFlowAssetStyles) => {
  const { width, height, zIndex, marginTop, marginBottom, marginRight, marginLeft } = inf;
  const styles = `
      background-size: contain;
      background-repeat: no-repeat;
      height: ${height}px;
      ${mapBaseStylesToCSS({
        width,
        zIndex,
        marginTop,
        marginBottom,
        marginRight,
        marginLeft,
      })}
  `;
  return styles;
};

const mapOtherBreakpointsToStyles = <T>(
  b: CustomBreakpoint<T>,
  mapper: (t: T) => string
): SerializedStyles => {
  return css`
    ${Object.keys(b).map(
      q => `@media (max-width: ${q}px) {${mapper(b[(q as unknown) as keyof typeof b])}}`
    )}
  `;
};

export const mapAbsoluteAssetViewToStyledImg = (
  a: ResponsiveAssetStyles<WAbsoluteAssetStyles>
): StyledComponent<React.ImgHTMLAttributes<HTMLImageElement>> => {
  const { desktop, tablet, mobile, otherBreakpoints } = a;
  let modifiedTablet = tablet;
  if (desktop?.width.unit === 'px' && !tablet?.width) {
    modifiedTablet = {
      ...modifiedTablet,
      width: { unit: 'px', value: Math.floor((desktop?.width.value * 2) / 3) },
    };
  }
  let modifiedMobile = mobile;
  if (desktop?.width.unit === 'px' && !mobile?.width) {
    modifiedMobile = {
      ...modifiedMobile,
      width: { unit: 'px', value: Math.floor(desktop?.width.value / 3) },
    };
  }
  return styled.img`
    ${mapAbsoluteAssetStyleToCSS(desktop)}
    ${modifiedTablet && `${MEDIA_QUERY.TABLET} {${mapAbsoluteAssetStyleToCSS(modifiedTablet)}}`}
    ${modifiedMobile && `${MEDIA_QUERY.MOBILE} {${mapAbsoluteAssetStyleToCSS(modifiedMobile)}}`}
    ${
      otherBreakpoints
        ? mapOtherBreakpointsToStyles<WAbsoluteAssetStyles>(
            otherBreakpoints,
            mapAbsoluteAssetStyleToCSS
          )
        : ''
    }
  `;
};

export const mapPseudoElementToCSS = (e: WPseudoElementView | undefined): SerializedStyles | '' => {
  if (!e) return '';

  const {
    src,
    content,
    responsiveStyles: { desktop, tablet, mobile, otherBreakpoints },
  } = e;

  const aspectRatio = desktop.height / desktop.width.value;
  let modifiedTablet = tablet;
  if (desktop?.width.unit === 'px' && !tablet?.width) {
    const tabletWidth = Math.floor((desktop?.width.value * 2) / 3);
    const tabletHeight = Math.floor(aspectRatio * tabletWidth);
    modifiedTablet = {
      ...modifiedTablet,
      width: { unit: 'px', value: tabletWidth },
      height: tabletHeight,
    };
  }
  let modifiedMobile = mobile;
  if (desktop?.width.unit === 'px' && !mobile?.width) {
    const mobileWidth = Math.floor(desktop?.width.value / 3);
    const mobileHeight = Math.floor(aspectRatio * mobileWidth);
    modifiedMobile = {
      ...modifiedMobile,
      width: { unit: 'px', value: mobileWidth },
      height: mobileHeight,
    };
  }

  const orderedBreakpoints = Object.keys(otherBreakpoints || {}).sort();
  const otherBPDesktop = _pick(
    otherBreakpoints,
    orderedBreakpoints.filter(b => Number(b) > 1200)
  );
  const otherBPDesktopToTablet = _pick(
    otherBreakpoints,
    orderedBreakpoints.filter(b => Number(b) <= 1200 && Number(b) > 992)
  );
  const otherBPTabletToMobile = _pick(
    otherBreakpoints,
    orderedBreakpoints.filter(b => Number(b) <= 992 && Number(b) > 767)
  );
  const otherBPMobile = _pick(
    otherBreakpoints,
    orderedBreakpoints.filter(b => Number(b) <= 767)
  );
  // @ts-ignore
  const getCustomBreakpointStyles = b => {
    if (Object.keys(b).length === 0) {
      return '';
    }
    return mapOtherBreakpointsToStyles<WInFlowAssetStyles>(b, mapInFlowStylesToCSS);
  };

  return css`
    ${src ? `background-image: url(${src});` : ''}
    content: ${content || "' '"};
    ${getCustomBreakpointStyles(otherBPDesktop)}
    ${mapInFlowStylesToCSS(desktop)}
    ${getCustomBreakpointStyles(otherBPDesktopToTablet)}
    ${modifiedTablet ? `${MEDIA_QUERY.TABLET} {${mapInFlowStylesToCSS(modifiedTablet)}}` : ''}
    ${getCustomBreakpointStyles(otherBPTabletToMobile)}
    ${modifiedMobile ? `${MEDIA_QUERY.MOBILE} {${mapInFlowStylesToCSS(modifiedMobile)}}` : ''}
    ${getCustomBreakpointStyles(otherBPMobile)}

  `;
};

export const mapBackgroundToCSS = (b?: WBackgroundView): SerializedStyles | '' => {
  if (!b) return '';
  const { background_color, background_image, background_repeat, fixed } = b;
  return css`
background-color: ${background_color};
${background_image ? `background-image: url(${background_image});` : ''}
background-repeat: ${background_repeat ? 'repeat' : 'no-repeat'};
background-size: contain;
background-attachment: ${fixed ? 'fixed' : 'inherit'};
`;
};
